"use client";

import { aboutMe } from "#/data";
import * as m from "motion/react-m";
import { Mail, MapPin, Clock, FileDown } from "lucide-react";
import { Button } from "#/components/ui/button";
import Link from "next/link";
import Image from "next/image";
import { Card, CardContent, CardHeader, CardTitle } from "#/components/ui/card";
import { MagicCard } from "#/components/magicui/magic-card";
import { ShineBorder } from "#/components/magicui/shine-border";
import dynamic from "next/dynamic";
const Section = dynamic(() => import("./section"), {
  ssr: false,
});

export const AboutSection = () => {
  return (
    <Section id="about" title={"A propos de moi"}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-4 lg:row-span-2"
        >
          <Card className="h-full border-none p-0 shadow-none">
            <MagicCard gradientColor={"#00d49220"} className="h-full">
              <CardHeader className="p-6 pb-3">
                <CardTitle className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                  {aboutMe.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="mb-6 flex flex-col items-center">
                  <div className="relative mb-4 h-40 w-40">
                    {/* Gradient border */}
                    <div className="absolute inset-1 rounded-full p-0.5">
                      <ShineBorder shineColor={["#00bba7", "#00bc7d", "#FFBE7B", "#FFBE7B"]} borderWidth={2} />
                      {/* Image container */}
                      <div className="relative h-full w-full overflow-hidden rounded-full">
                        <Image
                          src="/images/about-me.webp"
                          alt={aboutMe.name}
                          sizes="(max-width: 768px) 160px, 160px"
                          className="object-cover"
                          quality={75}
                          fill
                          priority
                        />
                        <div className="absolute inset-0 z-10 bg-transparent dark:shadow-[inset_0_0_30px_20px_rgba(0,0,0,0.6)]"></div>
                      </div>
                    </div>
                  </div>
                  <p className="mb-2 text-center text-lg font-medium text-emerald-600 dark:text-emerald-400">
                    {aboutMe.title}
                  </p>
                  <div className="mx-auto mb-4 h-1 w-20 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500"></div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-neutral-700 dark:text-neutral-300">
                    <MapPin className="h-5 w-5 flex-shrink-0 text-emerald-500" />
                    <span>{aboutMe.location}</span>
                  </div>

                  <div className="flex items-center gap-3 text-neutral-700 dark:text-neutral-300">
                    <Mail className="h-5 w-5 flex-shrink-0 text-emerald-500" />
                    <a href={`mailto:${aboutMe.email}`} className="transition-colors hover:text-emerald-500">
                      {aboutMe.email}
                    </a>
                  </div>

                  <div className="flex items-center gap-3 text-neutral-700 dark:text-neutral-300">
                    <Clock className="h-5 w-5 flex-shrink-0 text-emerald-500" />
                    <span>{aboutMe.availability}</span>
                  </div>

                  <div className="pt-2">
                    <h3 className="mb-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Langues</h3>
                    <div className="space-y-2">
                      {aboutMe.languages.map((lang, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-neutral-800 dark:text-neutral-200">
                          <span className="text-lg">{lang.emoji}</span>
                          <span>{lang.name}</span>
                          <span className="ml-auto text-xs font-medium text-emerald-600 dark:text-emerald-400">
                            {lang.level}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2">
                    <Button variant="outline" className="flex w-full content-center rounded-full" size="sm">
                      <FileDown className="mr-2 h-4 w-4" />
                      <Link href="/cv/cv.pdf" target="_blank" className={"w-full"} rel="noopener noreferrer">
                        Télécharger mon CV
                      </Link>
                    </Button>
                  </div>

                  <div className="mt-2 border-t border-neutral-200 pt-4 dark:border-neutral-800">
                    <h3 className="mb-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Qualités</h3>
                    <div className="flex flex-wrap gap-2">
                      {aboutMe.personalQualities.map((quality, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300"
                        >
                          {quality}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </MagicCard>
          </Card>
        </m.div>

        {/* Biographie - Colonne droite en haut */}
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="lg:col-span-8 lg:col-start-5"
        >
          <Card className="h-full border-none p-0 shadow-none">
            <MagicCard gradientColor={"#00d49220"} className="h-full">
              <CardHeader className="p-6 pb-3">
                <CardTitle className="text-xl font-bold text-neutral-900 dark:text-neutral-100">Biographie</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="prose dark:prose-invert prose-emerald">
                  <p className="mb-6 text-lg leading-relaxed text-neutral-700 dark:text-neutral-200">{aboutMe.bio}</p>

                  <h3 className="mt-4 mb-3 text-lg font-medium text-emerald-600 dark:text-emerald-400">
                    🎯 Mes forces :
                  </h3>

                  <ul className="mb-6 space-y-2 pl-2">
                    {aboutMe.strengths.map((strength, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="flex-shrink-0 text-lg text-emerald-500 dark:text-emerald-400">✅</span>
                        <span className="text-neutral-700 dark:text-neutral-200">{strength}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-4 rounded-md border-l-2 border-emerald-500 bg-neutral-50 p-3 dark:bg-neutral-800/50">
                    <p className="m-0 flex items-start gap-2 text-neutral-700 dark:text-neutral-200">
                      <span className="flex-shrink-0 text-lg text-amber-500 dark:text-amber-400">💡</span>
                      {aboutMe.note}
                    </p>
                  </div>
                </div>
              </CardContent>
            </MagicCard>
          </Card>
        </m.div>

        {/* Centres d'intérêt - Colonne droite en bas */}
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="lg:col-span-8 lg:col-start-5"
        >
          <Card className="h-full border-none p-0 shadow-none">
            <MagicCard gradientColor={"#00d49220"} className="h-full">
              <CardHeader className="p-6 pb-3">
                <CardTitle className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                  Centres d&apos;intérêt
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-3">
                  {aboutMe.interests.map((interest, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-100 px-3 py-1 text-sm text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
                    >
                      <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                      {interest}
                    </span>
                  ))}
                </div>
              </CardContent>
            </MagicCard>
          </Card>
        </m.div>
      </div>
    </Section>
  );
};
